import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { ArrowLeft, Save, Eye } from 'lucide-react'
import toast from 'react-hot-toast'

const CategoryAdd = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })
  const [loading, setLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  
  const { addCategory } = useFirebase()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Please enter a category name')
      return
    }
    
    if (formData.name.trim().length < 2) {
      toast.error('Category name must be at least 2 characters long')
      return
    }

    setLoading(true)
    
    try {
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim()
      }
      
      await addCategory(categoryData)
      toast.success('Category added successfully')
      navigate('/categories')
    } catch (error) {
      console.error('Error adding category:', error)
      toast.error('Failed to add category')
    } finally {
      setLoading(false)
    }
  }

  const fillExample = (name, description) => {
    setFormData({ name, description })
    setShowPreview(true)
  }

  const nursingCategories = [
    {
      name: 'Fundamentals of Nursing',
      description: 'Basic nursing principles, patient care, safety, and infection control'
    },
    {
      name: 'Medical-Surgical Nursing',
      description: 'Adult health conditions, surgical procedures, and post-operative care'
    },
    {
      name: 'Pharmacology',
      description: 'Drug classifications, mechanisms of action, and nursing considerations'
    },
    {
      name: 'Maternal & Child Health',
      description: 'Pregnancy, childbirth, newborn care, and pediatric nursing'
    },
    {
      name: 'Mental Health Nursing',
      description: 'Psychiatric conditions, therapeutic communication, and mental health interventions'
    },
    {
      name: 'Community Health',
      description: 'Public health, health promotion, and disease prevention in communities'
    }
  ]

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New Category</h1>
          <p className="text-sm text-gray-600">
            Create a new category to organize your questions
          </p>
        </div>
        <Link to="/categories" className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Categories
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Category Details</h3>
            </div>
            
            <div className="card-body">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Name */}
                <div>
                  <label htmlFor="name" className="form-label">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    placeholder="Enter category name (e.g., Fundamentals of Nursing)"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Choose a clear, descriptive name for this category
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="form-label">
                    Description (Optional)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    className="form-textarea"
                    placeholder="Provide a brief description of what this category covers..."
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Help users understand what topics this category includes
                    </p>
                    <span className="text-xs text-gray-400">
                      {formData.description.length}/500
                    </span>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="loading-spinner h-4 w-4 mr-2"></div>
                        Creating...
                      </div>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Create Category
                      </>
                    )}
                  </button>
                  <Link to="/categories" className="btn-outline">
                    Cancel
                  </Link>
                </div>
              </form>
            </div>
          </div>

          {/* Preview Card */}
          {formData.name && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Category Preview
                  </h3>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="btn-outline text-sm"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </button>
                </div>
              </div>
              
              {showPreview && (
                <div className="card-body">
                  <div className="border-l-4 border-l-primary-500 pl-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      {formData.name}
                    </h4>
                    {formData.description && (
                      <p className="text-sm text-gray-600 mb-3">
                        {formData.description}
                      </p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <span className="h-4 w-4 mr-1">📝</span>
                        0 questions
                      </span>
                      <span className="flex items-center">
                        <span className="h-4 w-4 mr-1">📅</span>
                        {new Date().toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Category Examples */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Category Examples</h3>
          </div>
          <div className="card-body">
            <p className="text-sm text-gray-600 mb-4">
              Here are some common nursing exam categories you might want to create:
            </p>
            
            <div className="space-y-3">
              {nursingCategories.map((category, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => fillExample(category.name, category.description)}
                >
                  <h4 className="font-medium text-gray-900">{category.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-gray-500 mt-4">
              💡 Click on any example above to use it as a template
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CategoryAdd
